import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  ColumnDef,
  ColumnSizingState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  Table,
  Updater,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { Checkbox } from '@ui/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShiftRangeRowSelection } from '../../../app-table/lib/hooks/use-shift-range-row-selection';
import {
  getDefaultRowId,
  getGlobalFilterFn,
  handleRowSelectionChange,
} from '../utils/table-helpers';
import { useVisibility } from './use-visiblility';

type UseDefaultTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  isResizable?: boolean;
  hiddenColumns?: string[];
  enableColumnFilter?: boolean;
  disablePagination?: boolean;
  rowSelection?: RowSelectionState;
  columnPinning?: {
    left?: string[];
    right?: string[];
  };
  setRowSelection?: (rowSelection: RowSelectionState) => void;
  getRowId?: (row: TData) => string;
  isTemplate?: boolean;
  defaultSorting?: SortingState;
  pagination?: PaginationState;
  sorting?: SortingState;
  totalRows?: number;
  manualPagination?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  onSearchChange?: (search: string) => void;
  onSortingChange?: (sorting: SortingState) => void;
};

export const useDefaultTable = <TData,>({
  data,
  columns,
  isResizable,
  hiddenColumns,
  enableColumnFilter,
  rowSelection,
  setRowSelection,
  getRowId,
  disablePagination,
  columnPinning,
  isTemplate,
  defaultSorting,
  pagination: controlledPagination,
  sorting: controlledSorting,
  totalRows,
  manualPagination = false,
  onPaginationChange,
  onSearchChange,
  onSortingChange,
}: UseDefaultTableProps<TData>) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    {
      pageIndex: 0,
      pageSize: 20,
    },
  );
  const [columnOrder, setColumnOrder] = useState<string[]>(
    columns.map((column) => column.id!),
  );
  const [internalColumnSorting, setInternalColumnSorting] =
    useState<SortingState>(defaultSorting ?? []);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const defaultHidden: Record<string, boolean> = useMemo(
    () =>
      hiddenColumns?.reduce(
        (acc, key) => {
          acc[key] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      ) ?? {},
    [hiddenColumns],
  );

  const [columnVisibility, setColumnVisibility] = useState(defaultHidden);
  const pagination = controlledPagination ?? internalPagination;
  const columnSorting = controlledSorting ?? internalColumnSorting;
  const isManualPagination = manualPagination && totalRows !== undefined;
  const isManualSorting = !!onSortingChange;

  const { columnStateSaveId } = useVisibility();
  const {
    handleRowCheckedChange,
    handleSelectCheckboxClick,
    handleSelectCheckboxKeyDown,
  } = useShiftRangeRowSelection<TData>({ rowSelection, setRowSelection });

  const tableColumns = useMemo<ColumnDef<TData, unknown>[]>(() => {
    if (rowSelection !== undefined && setRowSelection) {
      return [
        {
          cell: ({ row, table }: { row: Row<TData>; table: Table<TData> }) => (
            <div className="flex size-full items-center justify-center">
              <Checkbox
                checked={row.getIsSelected()}
                className="-ml-2"
                disabled={!row.getCanSelect()}
                onCheckedChange={(value) =>
                  handleRowCheckedChange(row, table, value)
                }
                onClick={handleSelectCheckboxClick}
                onKeyDown={handleSelectCheckboxKeyDown}
              />
            </div>
          ),
          header: ({ table }: { table: Table<TData> }) => (
            <div className="flex size-full items-center justify-center">
              <Checkbox
                checked={table.getIsAllPageRowsSelected()}
                onCheckedChange={(value) =>
                  table.toggleAllRowsSelected(!!value)
                }
              />
            </div>
          ),
          id: 'select',
          maxSize: 40,
          meta: {
            isServiceColumn: true,
          },
          size: 40,
        } as ColumnDef<TData, unknown>,
        ...columns,
      ];
    }

    return columns;
  }, [
    columns,
    handleRowCheckedChange,
    handleSelectCheckboxClick,
    handleSelectCheckboxKeyDown,
    rowSelection,
    setRowSelection,
  ]);

  const _getRowId = useMemo(() => getRowId || getDefaultRowId, [getRowId]);

  const handleColumnVisibilityChange = useCallback(
    (updater: Updater<VisibilityState>) => {
      setColumnVisibility((prev) => {
        const newVisibility =
          typeof updater === 'function' ? updater(prev) : updater;

        if (columnStateSaveId && !isTemplate) {
          localStorage.setItem(
            columnStateSaveId,
            JSON.stringify(newVisibility),
          );
        }

        return newVisibility;
      });
    },
    [columnStateSaveId],
  );

  const handlePaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      const nextPagination =
        typeof updater === 'function' ? updater(pagination) : updater;

      if (onPaginationChange) {
        onPaginationChange(nextPagination);
        return;
      }

      setInternalPagination(nextPagination);
    },
    [onPaginationChange, pagination],
  );

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const nextSorting =
        typeof updater === 'function' ? updater(columnSorting) : updater;

      if (onSortingChange) {
        onSortingChange(nextSorting);
        return;
      }

      setInternalColumnSorting(nextSorting);
    },
    [columnSorting, onSortingChange],
  );

  const handleGlobalFilterChange = useCallback(
    (updater: Updater<string>) => {
      const nextValue =
        typeof updater === 'function' ? updater(globalFilter) : updater;
      const searchValue = String(nextValue);

      setGlobalFilter(searchValue);
      onSearchChange?.(searchValue.trim());
    },
    [globalFilter, onSearchChange],
  );

  const table = useReactTable({
    autoResetExpanded: false,
    autoResetPageIndex: false,
    columnResizeMode: 'onChange',
    columns: tableColumns,
    data,
    defaultColumn: {
      filterFn: 'includesString',
      maxSize: 500,
      minSize: 50,
      size: 150,
    },
    enableColumnFilters: enableColumnFilter,
    enableColumnResizing: isResizable,
    enableGlobalFilter: true,
    filterFromLeafRows: true,
    globalFilterFn: getGlobalFilterFn<TData>(),
    state: {
      columnPinning: {
        left: ['select', ...(columnPinning?.left ?? [])],
        right: ['actions', ...(columnPinning?.right ?? [])],
      },
      ...(disablePagination ? {} : { pagination }),
      columnOrder,
      ...(rowSelection !== undefined ? { rowSelection } : {}),
      columnSizing,
      columnVisibility,
      globalFilter,
      sorting: columnSorting,
    },
    ...(isManualPagination
      ? {
          pageCount: Math.max(1, Math.ceil(totalRows / pagination.pageSize)),
        }
      : {}),
    getCoreRowModel: getCoreRowModel(),
    getRowId: _getRowId,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    onRowSelectionChange: (updater) =>
      handleRowSelectionChange(updater, rowSelection, setRowSelection),
    onSortingChange: handleSortingChange,
    ...(isManualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    ...(disablePagination || isManualPagination
      ? {}
      : {
          getPaginationRowModel: getPaginationRowModel(),
        }),
    ...(disablePagination
      ? {}
      : {
          onPaginationChange: handlePaginationChange,
        }),
    ...(isManualPagination
      ? {
          manualPagination: true,
        }
      : {}),
    ...(isManualSorting
      ? {
          manualSorting: true,
        }
      : {}),
    ...(onSearchChange
      ? {
          manualFiltering: true,
        }
      : {}),
  });

  const isEmpty = useMemo(() => {
    return !table.getRowModel().rows.length;
  }, [table.getRowModel().rows]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string);
        const newIndex = columnOrder.indexOf(over.id as string);
        return arrayMove(columnOrder, oldIndex, newIndex);
      });
    }
  }, []);

  useEffect(() => {
    if (columnStateSaveId && !isTemplate) {
      const columnState = localStorage.getItem(columnStateSaveId);
      if (columnState) {
        try {
          const savedVisibility = JSON.parse(columnState);
          setColumnVisibility(savedVisibility);
          table.setColumnVisibility(savedVisibility);
        } catch (error) {
          console.warn('Failed to parse saved column visibility state:', error);
        }
      }
    }
  }, [columnStateSaveId]);

  return {
    columnOrder,
    columnSizing,
    handleDragEnd,
    isEmpty,
    pagination,
    table,
    tableColumns,
  };
};
